import * as api from "./client";
import type { AddTranslations } from "@/hooks/useTranslations.js";
import { downloadBlob } from "@/utils/csvExport";
import type { TaxonomyMode } from "./submissions.service";

export type Referee = {
  // Referee fields
  id: string;
  title: string | null;
  firstName: string;
  surname: string;
  emailAddress: string;
  companyName: string;
  refereeCategory: string | null;
  barristerName: string | null;
  code: string;

  // Submission context
  submissionId: string;
  firmRef: string;
  firmName: string;
  regionName: string | null;
  publicationName: string;
  edition: string | null;

  // Practice hierarchy
  practiceAreaName: string | null;
  practiceGroupName: string | null;
  tableName: string | null;
  practiceAreaId: number | null;
  practiceGroupId: number | null;
  regionId: number | null;
  tableId: number | null;

  // Dates and status
  createdAt: string;
  refereeCreatedAt: string;
  refereeCreatedBy: string | null;
  refereeCreatedByEmail: string | null;
  subtype: string | null;
  editionId: string | null;
  rankingEntityId: string | null;
  bpaId: string | null;
  surveyAnswered: boolean;
  responseDate: string | null;
};

export type RefereeWithTranslations = AddTranslations<Referee>;

export type RefereesFilters = {
  limit?: number;
  offset?: number;
  includeDeleted?: boolean;
  publicationId?: number;
  edition?: string;
  regionId?: number;
  editionId?: string;
  rankingEntityId?: string;
  bpaId?: string;
  taxonomy?: TaxonomyMode;
  search?: string;
  firmRef?: string;
  surveyAnswered?: "yes" | "no" | "all";
  skipCount?: boolean;
};

export type RefereesResponse = {
  referees: Referee[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  requestId?: string;
};

export type RefereesExportFilters = Omit<RefereesFilters, "limit" | "offset" | "skipCount">;

export type RefereeByRegion = {
  id: string;
  title: string | null;
  firstName: string;
  surname: string;
  emailAddress: string;
  companyName: string;
  refereeCategory: string | null;
  country: string | null;
  regionId: number | null;
  regionName: string | null;
};

export type RefereeScope =
  | { publicationId: number; regionIds: number[] }
  | { editionId: string; rankingEntityIds: string[] };

export type RefereesByRegionsBody = RefereeScope & {
  search?: string;
  refereeCategory?: string;
  limit?: number;
  offset?: number;
};

export type RefereesByRegionsResponse = {
  uniqueEmails: number;
  referees: RefereeByRegion[];
  breakdown: {
    byFirstLetter: Record<string, number>;
    byCategory: { instructing: number; other: number; unknown: number };
  };
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  requestId: string | null;
};

function buildRefereesQueryParams(filters?: Partial<RefereesFilters>): URLSearchParams {
  const queryParams = new URLSearchParams();

  if (filters?.limit !== undefined) {
    queryParams.append("limit", String(filters.limit));
  }
  if (filters?.offset !== undefined) {
    queryParams.append("offset", String(filters.offset));
  }
  if (filters?.includeDeleted) {
    queryParams.append("includeDeleted", "true");
  }
  if (filters?.publicationId !== undefined) {
    queryParams.append("publicationId", String(filters.publicationId));
  }
  if (filters?.edition !== undefined) {
    queryParams.append("edition", String(filters.edition));
  }
  if (filters?.regionId !== undefined) {
    queryParams.append("regionId", String(filters.regionId));
  }
  if (filters?.editionId !== undefined) {
    queryParams.append("editionId", filters.editionId);
  }
  if (filters?.rankingEntityId !== undefined) {
    queryParams.append("rankingEntityId", filters.rankingEntityId);
  }
  if (filters?.bpaId !== undefined) {
    queryParams.append("bpaId", filters.bpaId);
  }
  if (filters?.search) {
    queryParams.append("search", filters.search);
  }
  if (filters?.firmRef) {
    queryParams.append("firmRef", filters.firmRef);
  }
  if (filters?.surveyAnswered && filters.surveyAnswered !== "all") {
    queryParams.append("surveyAnswered", filters.surveyAnswered);
  }
  if (filters?.skipCount) {
    queryParams.append("skipCount", "true");
  }
  if (filters?.taxonomy !== undefined) {
    queryParams.append("taxonomy", filters.taxonomy);
  }

  return queryParams;
}

function getFilenameFromContentDisposition(
  contentDisposition: string | null,
  fallback: string,
): string {
  if (!contentDisposition) return fallback;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const basicMatch = contentDisposition.match(/filename="([^"]+)"/i);
  if (basicMatch?.[1]) {
    return basicMatch[1];
  }

  return fallback;
}

export const refereesService = {
  async exportCsv(filters?: RefereesExportFilters): Promise<void> {
    const queryString = buildRefereesQueryParams(filters).toString();
    const endpoint = `/v1/submission/referees/stream${queryString ? `?${queryString}` : ""}`;
    const response = await api.fetchRaw(endpoint);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const fallbackFilename = `referees-export-${new Date().toISOString().split("T")[0]}.csv`;
    const filename = getFilenameFromContentDisposition(
      response.headers.get("content-disposition"),
      fallbackFilename,
    );

    downloadBlob(blob, filename);
  },

  async get(filters?: RefereesFilters): Promise<RefereesResponse> {
    const queryString = buildRefereesQueryParams(filters).toString();
    const endpoint = `/v1/submission/referees${queryString ? `?${queryString}` : ""}`;

    return api.get<RefereesResponse>(endpoint);
  },

  async getRefereesByRegions(body: RefereesByRegionsBody): Promise<RefereesByRegionsResponse> {
    return api.post<RefereesByRegionsResponse>("/v1/submission/referees/by-regions", body);
  },
};
